import React, { useEffect, useState } from 'react';
import ContentCard from '../Dashboard/ContentCard';
import EventCard from '../Card/EventCard';
import MatchCard from '../Card/MatchCard';
import { ScrapedEvent } from '../../types/api/ScrapedEvent';
import { FootballMatch } from '../../types/api/FootballMatch';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import VacancesMoselle from './VacancesMoselle';
import { useTheme } from '@mui/material/styles';

interface Props {
  WeatherSlide: React.ComponentType<{ city: string }>;
  matches: FootballMatch[];
  matchesLoading: boolean;
  onRefreshMatches: () => void;
  events: ScrapedEvent[];
  eventsLoading: boolean;
  onRefreshEvents: () => void;
}


const TabInformations: React.FC<Props> = ({ WeatherSlide, matches, matchesLoading, onRefreshMatches, events, eventsLoading, onRefreshEvents }) => {
  const theme = useTheme();
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <VacancesMoselle />

      <ContentCard
        title="Matchs FC Metz"
        subtitle="Prochains matchs"
        actions={
          <div className="flex items-center gap-1">
            <IconButton onClick={onRefreshMatches} disabled={matchesLoading} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        }
      >
        {matchesLoading ? (
          <div className="p-4">Chargement...</div>
        ) : matches.length > 0 ? (
          <CarouselMatches matches={matches} />
        ) : (
          <div className="text-sm" style={{ color: theme.palette.text.secondary }}>Aucun match à venir</div>
        )}
      </ContentCard>

      <ContentCard
        title="Événements à venir"
        actions={<IconButton onClick={onRefreshEvents} disabled={eventsLoading} size="small"><RefreshIcon fontSize="small" /></IconButton>}
      >
        {eventsLoading ? (
          <div className="p-4">Chargement...</div>
        ) : events.length > 0 ? (
          <div className="flex flex-col gap-2">
            {events.map((e, i) => <EventCard key={i} event={e} />)}
          </div>
        ) : (
          <div className="text-sm" style={{ color: theme.palette.text.secondary }}>Aucun événement à venir</div>
        )}
      </ContentCard>
    </div>
  );
};

export default TabInformations;

// --- CarouselMatches (inline helper component) ---
function CarouselMatches({ matches }: { matches: FootballMatch[] }) {
  const theme = useTheme();
  const [active, setActive] = useState(0);

  const toDate = (m: FootballMatch) => {
    try {
      if (m.dateIso) {
        const d = new Date(m.dateIso);
        return isNaN(d.getTime()) ? null : d;
      }

      // fallback: try to parse date and time strings
      const dateStr = m.date ?? '';
      const timeStr = m.time ?? '00:00';
      // if already ISO-like
      if (dateStr.includes('T')) {
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) return d;
      }

      // try simple YYYY-MM-DD or DD/MM/YYYY
      const d1 = new Date(`${dateStr} ${timeStr}`);
      if (!isNaN(d1.getTime())) return d1;

      // try french month name parsing (e.g., '7 décembre 2025')
      const dm = (dateStr || '').match(/(\d{1,2})\s+([A-Za-zéûàèâîôù]+)\s*(\d{4})?/i);
      if (dm) {
        const day = Number(dm[1]);
        const monthName = dm[2].toLowerCase();
        const monthMapLocal: Record<string, number> = { 'janvier':0,'février':1,'fevrier':1,'mars':2,'avril':3,'mai':4,'juin':5,'juillet':6,'août':7,'aout':7,'septembre':8,'octobre':9,'novembre':10,'décembre':11,'decembre':11 };
        const monthIdx = monthMapLocal[monthName] ?? (new Date()).getMonth();
        const year = dm[3] ? Number(dm[3]) : (new Date()).getFullYear();
        const tm = (timeStr || '').match(/(\d{1,2})h(\d{2})?/) || (timeStr || '').match(/(\d{1,2}):(\d{2})/);
        const hour = tm ? Number(tm[1]) : 0;
        const minute = tm && tm[2] ? Number(tm[2]) : 0;
        const d2 = new Date(year, monthIdx, day, hour, minute);
        return isNaN(d2.getTime()) ? null : d2;
      }

      return null;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    // choose first upcoming match
    const now = new Date();
    const idx = matches.findIndex(m => {
      const d = toDate(m);
      return !!d && d.getTime() >= now.getTime();
    });
    setActive(idx >= 0 ? idx : 0);
  }, [matches]);

  if (!matches || matches.length === 0) return <div className="text-sm" style={{ color: theme.palette.text.secondary }}>Aucun match à venir</div>;

  const prev = () => setActive((a) => (a - 1 + matches.length) % matches.length);
  const next = () => setActive((a) => (a + 1) % matches.length);

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center space-x-2">
        <IconButton size="small" onClick={prev} aria-label="previous" disabled={matches.length <= 1}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>

        <div style={{ minWidth: 260, maxWidth: 520, width: '100%' }}>
          <MatchCard match={matches[active]} />
        </div>

        <IconButton size="small" onClick={next} aria-label="next" disabled={matches.length <= 1}>
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </div>

      <div className="mt-2 text-sm" style={{ color: theme.palette.text.secondary }}>{active + 1} / {matches.length}</div>
    </div>
  );
}